import React, { useCallback, useEffect, useState } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, ActivityIndicator, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Header, FormHeader, ScreenLayout, colors, spacing, typography } from '../components';
import { BusinessUserPicker, Department } from '../components/BusinessUserPicker';
import { useAppContext } from '../contexts/AppContext';
import { useLocale } from '../contexts/LocaleContext';
import { createTabPressHandler } from '../navigation/handleTabPress';
import { apiService, BusinessUser, Visit, VisitCampaign } from '../services/api';
import { useThemedStyles } from '../hooks/useThemedStyles';

interface DistributionByUser {
  userId: string;
  visits: Visit[];
}

export const VisitCampaignDistributeScreen: React.FC = () => {
  const styles = useThemedStyles(getStyles);
  const { navigateTo, routeParams, showNotification, activeTab, setActiveTab } = useAppContext();
  const { t } = useLocale();
  const handleTabPress = createTabPressHandler(navigateTo, setActiveTab);

  const campaignId: string | undefined = routeParams?.campaignId;

  const [campaign, setCampaign] = useState<VisitCampaign | null>(null);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [userNames, setUserNames] = useState<Record<string, string>>({});
  const [selectedDepartmentId, setSelectedDepartmentId] = useState<string | null>(null);

  const [unassigned, setUnassigned] = useState<Visit[]>([]);
  const [byUser, setByUser] = useState<DistributionByUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAutoDistributing, setIsAutoDistributing] = useState(false);

  const [showAssignPicker, setShowAssignPicker] = useState(false);
  const [assignTargetVisitId, setAssignTargetVisitId] = useState<string | null>(null);
  const [isReassigning, setIsReassigning] = useState(false);

  const loadCampaign = useCallback(async () => {
    if (!campaignId) return;
    try {
      const res = await apiService.getVisitCampaign(campaignId);
      if (res.statusCode === 1000 && res.data) {
        setCampaign(res.data);
        if (res.data.assignedDepartmentIds?.length === 1) {
          setSelectedDepartmentId(res.data.assignedDepartmentIds[0]);
        }
        if (res.data.assignedDepartmentIds?.length) {
          const deptRes = await apiService.getDepartments(res.data.businessId);
          if (deptRes.statusCode === 1000 && Array.isArray(deptRes.data)) {
            const filtered = (deptRes.data as Department[]).filter((d) => res.data.assignedDepartmentIds.includes(d._id));
            setDepartments(filtered);
          }
        }
        const userRes = await apiService.getBusinessUsers(res.data.businessId);
        if (userRes.statusCode === 1000 && Array.isArray(userRes.data)) {
          const map: Record<string, string> = {};
          (userRes.data as BusinessUser[]).forEach((u) => {
            const first = u.user?.firstName || '';
            const last = u.user?.lastName || '';
            map[u.userId] = (first || last) ? `${first} ${last}`.trim() : (u.user?.email || u.userId);
          });
          setUserNames(map);
        }
      }
    } catch (err) {
      console.warn('[VisitCampaignDistribute] loadCampaign error:', err);
    }
  }, [campaignId]);

  const loadDistribution = useCallback(async () => {
    if (!campaignId) return;
    setIsLoading(true);
    try {
      const res = await apiService.getVisitCampaignDistribution(campaignId);
      if (res.statusCode === 1000 && res.data) {
        setUnassigned(res.data.unassigned || []);
        setByUser(res.data.byUser || []);
      }
    } catch (err) {
      console.warn('[VisitCampaignDistribute] loadDistribution error:', err);
    } finally {
      setIsLoading(false);
    }
  }, [campaignId]);

  useEffect(() => {
    loadCampaign();
  }, [loadCampaign]);

  useEffect(() => {
    loadDistribution();
  }, [loadDistribution]);

  const handleAutoDistribute = async () => {
    if (!campaignId || !selectedDepartmentId) return;
    setIsAutoDistributing(true);
    try {
      const res = await apiService.autoDistributeVisitCampaign(campaignId, { departmentId: selectedDepartmentId });
      if (res.statusCode === 1000) {
        showNotification('success', t('visitCampaigns.distribute.title'), t('visitCampaigns.distribute.autoDistributed', { count: res.data?.assigned ?? 0 }));
        loadDistribution();
      } else {
        showNotification('error', t('visitCampaigns.distribute.title'), res.errors?.[0]?.message || t('visits.errors.generic'));
      }
    } catch (err: any) {
      showNotification('error', t('visitCampaigns.distribute.title'), err?.message || t('visits.errors.generic'));
    } finally {
      setIsAutoDistributing(false);
    }
  };

  const openAssignPicker = (visitId: string) => {
    setAssignTargetVisitId(visitId);
    setShowAssignPicker(true);
  };

  const handleAssignConfirm = async (ids: string[]) => {
    const userId = ids[0];
    setShowAssignPicker(false);
    if (!campaignId || !assignTargetVisitId || !userId) return;
    setIsReassigning(true);
    try {
      const res = await apiService.manualDistributeVisitCampaign(campaignId, {
        allocations: [{ visitId: assignTargetVisitId, assignedTo: userId }],
      });
      if (res.statusCode === 1000) {
        loadDistribution();
      } else {
        const rejected = res.data?.rejected?.[0];
        showNotification('error', t('visitCampaigns.distribute.title'), rejected?.reason || res.errors?.[0]?.message || t('visits.errors.generic'));
      }
    } catch (err: any) {
      showNotification('error', t('visitCampaigns.distribute.title'), err?.message || t('visits.errors.generic'));
    } finally {
      setIsReassigning(false);
      setAssignTargetVisitId(null);
    }
  };

  const contactLabel = (visit: Visit): string => visit.targetLocation?.address || `${t('visits.list.contact')} ${visit.contactId.slice(-6)}`;

  const renderVisitRow = (visit: Visit, actionLabel: string) => (
    <View key={visit._id} style={styles.visitRow}>
      <Text style={styles.visitRowText} numberOfLines={1}>{contactLabel(visit)}</Text>
      <TouchableOpacity style={styles.assignButton} onPress={() => openAssignPicker(visit._id)} disabled={isReassigning}>
        <Text style={styles.assignButtonText}>{actionLabel}</Text>
      </TouchableOpacity>
    </View>
  );

  if (!campaign) {
    return (
      <ScreenLayout
        header={
          <Header
            showNotifications={true}
            showMessages={true}
            onNotificationPress={() => navigateTo('notifications')}
            onMessagePress={() => navigateTo('chat')}
            showNavigation={Platform.OS === 'web'}
            activeTab={activeTab}
            onTabPress={handleTabPress}
          />
        }
        showBottomTabs={Platform.OS !== 'web'}
        onLeftPaneClose={() => {}}
        leftPaneContent="settings"
        onSettingsItemPress={async (itemId) => {
          const { setPendingSettingsItem } = await import('../utils/tempStorage');
          await setPendingSettingsItem(itemId);
          navigateTo('settings');
        }}
        showRightPane={false}
        formHeader={<FormHeader title={t('visitCampaigns.distribute.title')} onBack={() => navigateTo('visits-campaigns')} />}
      >
        <View style={styles.screen}>
          <View style={styles.centered}>
            <ActivityIndicator color={colors.primary as string} />
          </View>
        </View>
      </ScreenLayout>
    );
  }

  return (
    <ScreenLayout
      header={
        <Header
          showNotifications={true}
          showMessages={true}
          onNotificationPress={() => navigateTo('notifications')}
          onMessagePress={() => navigateTo('chat')}
          showNavigation={Platform.OS === 'web'}
          activeTab={activeTab}
          onTabPress={handleTabPress}
        />
      }
      showBottomTabs={Platform.OS !== 'web'}
      onLeftPaneClose={() => {}}
      leftPaneContent="settings"
      onSettingsItemPress={async (itemId) => {
        const { setPendingSettingsItem } = await import('../utils/tempStorage');
        await setPendingSettingsItem(itemId);
        navigateTo('settings');
      }}
      showRightPane={false}
      formHeader={<FormHeader title={t('visitCampaigns.distribute.title')} onBack={() => navigateTo('visit-campaign-detail', undefined, { campaignId: campaign._id })} />}
    >
      <View style={styles.screen}>
      <ScrollView contentContainerStyle={styles.content}>
        <View style={styles.formCard}>
          <Text style={styles.sectionTitle}>{t('visitCampaigns.distribute.autoSection')}</Text>
          {departments.length > 1 ? (
            <View style={styles.deptChipsRow}>
              {departments.map((d) => (
                <TouchableOpacity
                  key={d._id}
                  style={[styles.deptChip, selectedDepartmentId === d._id && styles.deptChipActive]}
                  onPress={() => setSelectedDepartmentId(d._id)}
                >
                  <Text style={[styles.deptChipText, selectedDepartmentId === d._id && styles.deptChipTextActive]}>{d.name}</Text>
                </TouchableOpacity>
              ))}
            </View>
          ) : departments.length === 1 ? (
            <Text style={styles.metaText}>{t('visitCampaigns.distribute.usingDepartment', { name: departments[0].name })}</Text>
          ) : null}
          <TouchableOpacity
            style={[styles.primaryButton, (!selectedDepartmentId || isAutoDistributing) && styles.disabledButton]}
            onPress={handleAutoDistribute}
            disabled={!selectedDepartmentId || isAutoDistributing}
          >
            {isAutoDistributing ? <ActivityIndicator color={colors.white as string} /> : <Text style={styles.primaryButtonText}>{t('visitCampaigns.distribute.autoDistributeButton')}</Text>}
          </TouchableOpacity>
        </View>

        {isLoading ? (
          <View style={styles.centered}>
            <ActivityIndicator color={colors.primary as string} />
          </View>
        ) : (
          <>
            <View style={styles.formCard}>
              <Text style={styles.sectionTitle}>{t('visitCampaigns.distribute.unassignedSection', { count: unassigned.length })}</Text>
              {unassigned.length === 0 ? (
                <Text style={styles.metaText}>{t('visitCampaigns.distribute.noUnassigned')}</Text>
              ) : (
                unassigned.map((v) => renderVisitRow(v, t('visitCampaigns.distribute.assign')))
              )}
            </View>

            {byUser.map((group) => (
              <View key={group.userId} style={styles.formCard}>
                <Text style={styles.sectionTitle}>{userNames[group.userId] || group.userId} ({group.visits.length})</Text>
                {group.visits.map((v) => renderVisitRow(v, t('visitCampaigns.distribute.reassign')))}
              </View>
            ))}
          </>
        )}
      </ScrollView>
      </View>

      <BusinessUserPicker
        visible={showAssignPicker}
        onClose={() => { setShowAssignPicker(false); setAssignTargetVisitId(null); }}
        onConfirm={handleAssignConfirm}
        mode="single"
        source="users"
        title={t('visitCampaigns.distribute.assign')}
      />
    </ScreenLayout>
  );
};

const getStyles = () => StyleSheet.create({
  screen: { flex: 1 },
  content: { padding: spacing.lg, paddingBottom: spacing.xl * 2 },
  centered: { flex: 1, justifyContent: 'center', alignItems: 'center', padding: spacing.xl },

  formCard: {
    backgroundColor: colors.background.surface as string,
    borderRadius: 12,
    padding: spacing.lg,
    borderWidth: 1,
    borderColor: colors.border as string,
    marginBottom: spacing.lg,
  },
  sectionTitle: {
    fontSize: typography.fontSize.xl,
    fontFamily: typography.fontFamily.semibold,
    color: colors.text.primary as string,
    marginBottom: spacing.sm,
  },
  metaText: { fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.regular, color: colors.text.secondary as string, marginBottom: spacing.sm },

  deptChipsRow: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.xs, marginBottom: spacing.sm },
  deptChip: {
    borderWidth: 1,
    borderColor: colors.border as string,
    borderRadius: 16,
    paddingVertical: spacing.xs,
    paddingHorizontal: spacing.md,
  },
  deptChipActive: { backgroundColor: colors.primary as string, borderColor: colors.primary as string },
  deptChipText: { fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.regular, color: colors.text.primary as string },
  deptChipTextActive: { color: colors.white as string, fontFamily: typography.fontFamily.semibold },

  primaryButton: { backgroundColor: colors.primary as string, borderRadius: 12, paddingVertical: spacing.md, alignItems: 'center' },
  primaryButtonText: { color: colors.white as string, fontSize: typography.fontSize.base, fontFamily: typography.fontFamily.semibold },
  disabledButton: { opacity: 0.6 },

  visitRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: colors.border as string,
  },
  visitRowText: { flex: 1, fontSize: typography.fontSize.base, fontFamily: typography.fontFamily.regular, color: colors.text.primary as string, marginRight: spacing.sm },
  assignButton: {
    borderWidth: 1,
    borderColor: colors.primary as string,
    borderRadius: 8,
    paddingVertical: spacing.xs,
    paddingHorizontal: spacing.sm,
  },
  assignButtonText: { fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.semibold, color: colors.primary as string },
});
